import React from "react";
import type { StyledRowBlock } from "../../../types/content";
import styles from "./StyledRow.module.css";

export interface StyledRowProps {
  block: StyledRowBlock;
  children?: React.ReactNode;
  className?: string;
}

/** Maps a semantic token to a CSS module class name */
function classFor(
  map: Record<string, string | undefined>,
  key: string | null | undefined
): string | undefined {
  if (!key) return undefined;
  return map[key];
}

const bgClasses = {
  cream: styles.bgCream,
  white: styles.bgWhite,
  gray: styles.bgGray,
} as const;

const textClasses = {
  default: styles.textDefault,
  white: styles.textWhite,
  dark: styles.textDark,
  primary: styles.textPrimary,
} as const;

const paddingClasses = {
  none: styles.paddingNone,
  small: styles.paddingSmall,
  medium: styles.paddingMedium,
  large: styles.paddingLarge,
  xl: styles.paddingXl,
} as const;

const marginClasses = {
  none: styles.marginNone,
  small: styles.marginSmall,
  medium: styles.marginMedium,
  large: styles.marginLarge,
} as const;

const borderClasses = {
  none: styles.borderNone,
  thin: styles.borderThin,
  medium: styles.borderMedium,
  thick: styles.borderThick,
} as const;

const radiusClasses = {
  none: styles.radiusNone,
  small: styles.radiusSmall,
  medium: styles.radiusMedium,
  large: styles.radiusLarge,
} as const;

/**
 * Styled Row Block - layout container that wraps child blocks with
 * configurable background, text color, padding, margin, border, and border radius.
 *
 * Child blocks are rendered by the BlockGrid's area rendering system;
 * this component only provides the styled wrapper.
 */
export const StyledRow: React.FC<StyledRowProps> = ({
  block,
  children,
  className,
}) => {
  const {
    backgroundColor,
    textColor,
    padding,
    margin,
    border,
    borderRadius,
    fullWidth,
    removeGapAbove,
  } = block;

  const containerClasses = [
    styles.styledRow,
    fullWidth ? styles.fullWidth : undefined,
    removeGapAbove ? styles.noGapAbove : undefined,
    classFor(bgClasses, backgroundColor),
    classFor(textClasses, textColor),
    classFor(paddingClasses, padding),
    classFor(marginClasses, margin),
    classFor(borderClasses, border),
    classFor(radiusClasses, borderRadius),
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={containerClasses}
      data-component="styled-row"
      data-bg={backgroundColor ?? undefined}
      data-text-color={textColor ?? undefined}
      data-full-width={fullWidth || undefined}
    >
      {fullWidth ? (
        <div className={styles.fullWidthInner}>{children}</div>
      ) : (
        children
      )}
    </section>
  );
};

export default StyledRow;
